import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { Heart, LayoutGrid, List, type LucideIconData } from 'lucide-angular';
import { Observable, catchError, map, of, startWith, switchMap } from 'rxjs';

import type { AddToCartEvent, Product } from '../../../../shared/models/product.model';
import type { Category } from '../../../../shared/models/category.model';
import { AuthService } from '../../../../shared/services/auth.service';
import { CartDrawerService } from '../../../../shared/services/cart-drawer.service';
import { CartService } from '../../../../shared/services/cart.service';
import { CategoryService } from '../../../../shared/services/category.service';
import { PrepurchaseModalService } from '../../../../shared/services/prepurchase-modal.service';
import { StockAlertModalService } from '../../../../shared/services/stock-alert-modal.service';
import { StockAlertStore } from '../../../../shared/services/stock-alert.store';
import { ToastService } from '../../../../shared/services/toast.service';
import { WishlistStore } from '../../../../shared/services/wishlist.store';
import {
  ButtonComponent,
  IconComponent,
  PageSizeSelectComponent,
  PaginationComponent,
  ProductCardComponent,
  ProductZoomModalComponent,
  SortDropdownComponent,
  type SortOption,
} from '../../../../shared/ui';
import { cartErrorMessage } from '../../../../shared/utils/cart.utils';
import { deriveProductCategorySlug } from '../../../catalog/data/catalog-mappers';
import { type ProfileWishlistEntry, ProfileWishlistService } from '../../data/profile-wishlist.service';
import { PROFILE_COPY } from '../../profile.copy';
import { formatIsoDateTimeAsDate } from '../../utils/profile-format.util';

type ViewMode = 'grid' | 'list';
type WishlistSortKey = 'date-desc' | 'date-asc' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc';

type ListState =
  | { status: 'signed-out' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; entries: ProfileWishlistEntry[] };

/** Lo que decide qué se pide: cambia la cuenta o se reintenta → petición nueva. */
interface ListKey {
  userId: number | null;
  attempt: number;
}

const sameKey = (a: ListKey, b: ListKey): boolean => a.userId === b.userId && a.attempt === b.attempt;

const tab = PROFILE_COPY.WISHLIST_TAB;

/**
 * «Lista de deseos» (`/perfil/deseos`, W2, §5.6.4 de B10): las tarjetas reales de `GET /profile/wishlist`, con el orden y
 * la paginación en cliente de la pestaña vieja (por defecto `added_at desc`, como la API).
 *
 * **El estado pertenece a la cuenta** (como W1): todo sale de una clave con `currentUser()?.id`; al cambiar (A → B,
 * con o sin `null` en medio) el `switchMap` cancela la petición de la cuenta anterior y nada de A se pinta con B.
 *
 * **Se pinta lo cargado ∩ `WishlistStore.ids`** (cuando el store ya tiene los ids de la cuenta): quitar desde el
 * corazón de la tarjeta la oculta al instante, y si el store revierte (error) la tarjeta vuelve. Quitar es el corazón:
 * no hay botón propio. La campana es la `StockAlertStore` de la tarjeta (B18); el quickview la lee del mismo store.
 */
@Component({
  selector: 'app-profile-wishlist',
  imports: [
    ButtonComponent,
    IconComponent,
    NgOptimizedImage,
    PageSizeSelectComponent,
    PaginationComponent,
    ProductCardComponent,
    ProductZoomModalComponent,
    RouterLink,
    SortDropdownComponent,
  ],
  templateUrl: './profile-wishlist.component.html',
  styleUrl: './profile-wishlist.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileWishlistComponent {
  private readonly wishlist = inject(ProfileWishlistService);
  private readonly store = inject(WishlistStore);
  private readonly currentUser = inject(AuthService).currentUser;
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cartService = inject(CartService);
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly toastService = inject(ToastService);
  private readonly prepurchaseModalService = inject(PrepurchaseModalService);
  private readonly stockAlertModal = inject(StockAlertModalService);
  private readonly stockAlertStore = inject(StockAlertStore);
  private readonly roots = toSignal(inject(CategoryService).getRootCategoriesCached(), {
    initialValue: [] as Category[],
  });

  protected readonly copy = PROFILE_COPY.WISHLIST;
  protected readonly tabCopy = tab;
  protected readonly retryLabel = PROFILE_COPY.ORDERS.RETRY;
  protected readonly heartIcon: LucideIconData = Heart;
  protected readonly gridIcon: LucideIconData = LayoutGrid;
  protected readonly listIcon: LucideIconData = List;

  protected readonly sortOptions: SortOption[] = [
    { value: 'date-desc', label: tab.SORT.DATE_DESC },
    { value: 'date-asc', label: tab.SORT.DATE_ASC },
    { value: 'price-asc', label: tab.SORT.PRICE_ASC },
    { value: 'price-desc', label: tab.SORT.PRICE_DESC },
    { value: 'name-asc', label: tab.SORT.NAME_ASC },
    { value: 'name-desc', label: tab.SORT.NAME_DESC },
  ];

  protected readonly viewMode = signal<ViewMode>('grid');
  protected readonly sortKey = signal<WishlistSortKey>('date-desc');
  protected readonly currentPage = signal(1);
  protected readonly perPage = signal(12);
  /** El quickview guarda el id: el producto se relee de lo cargado (y sigue abierto aunque se quite del corazón). */
  protected readonly zoomProductId = signal<Product['id'] | null>(null);
  private readonly attempt = signal(0);

  private readonly key = computed<ListKey>(
    () => ({ userId: this.currentUser()?.id ?? null, attempt: this.attempt() }),
    { equal: sameKey },
  );

  protected readonly state = toSignal(toObservable(this.key).pipe(switchMap((key) => this.load(key))), {
    initialValue: { status: 'loading' } as ListState,
  });

  private readonly entries = computed(() => {
    const state = this.state();
    return state.status === 'loaded' ? state.entries : [];
  });

  /** Lo cargado que sigue guardado en el store (con sus ids ya cargados; si no, todo lo cargado). */
  private readonly visible = computed(() => {
    const entries = this.entries();
    if (!this.store.loaded()) return entries;
    const ids = this.store.ids();
    return entries.filter((entry) => ids.has(Number(entry.product.id)));
  });

  protected readonly sorted = computed(() => sortEntries(this.visible(), this.sortKey()));
  protected readonly total = computed(() => this.sorted().length);
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.total() / this.perPage())));
  /** Al quitar la última tarjeta de la última página, se queda en la que ya existe. */
  protected readonly page = computed(() => Math.min(this.currentPage(), this.totalPages()));
  protected readonly paginated = computed(() => {
    const start = (this.page() - 1) * this.perPage();
    return this.sorted().slice(start, start + this.perPage());
  });

  protected readonly countLabel = computed(() =>
    (this.total() === 1 ? tab.COUNT_ONE : tab.COUNT_OTHER).replace('{n}', String(this.total())),
  );

  protected readonly zoomProduct = computed<Product | null>(() => {
    const id = this.zoomProductId();
    if (id === null) return null;
    return this.entries().find((entry) => entry.product.id === id)?.product ?? null;
  });
  protected readonly zoomNotifyActive = computed(() => this.stockAlertStore.hasAlert(this.zoomProduct()?.id));

  protected addedLabel(entry: ProfileWishlistEntry): string {
    const date = formatIsoDateTimeAsDate(entry.addedAt);
    return date ? tab.ADDED_AT.replace('{fecha}', date) : '';
  }

  protected retry(): void {
    this.attempt.update((n) => n + 1);
  }

  protected onSortChange(sort: string): void {
    this.sortKey.set(sort as WishlistSortKey);
    this.currentPage.set(1);
  }

  protected onPageSizeChange(size: number): void {
    this.perPage.set(size);
    this.currentPage.set(1);
  }

  protected onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  // ── Compra, aviso y quickview (mismo cableado que el catálogo) ───

  protected onAddToCart(event: AddToCartEvent): void {
    this.cartService
      .addLine(Number(event.product.id), event.quantity, event.product)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.cartDrawerService.open(),
        error: (err) => {
          // `null` = bloqueo de Acceso VIP: el diálogo ya lo explica.
          const message = cartErrorMessage(err, 'No pudimos agregar el producto al carrito.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }

  protected onZoomAddToCart(event: AddToCartEvent): void {
    this.onAddToCart(event);
    this.zoomProductId.set(null);
  }

  protected onReserve(product: Product): void {
    this.prepurchaseModalService.open(product);
  }

  protected onZoomReserve(event: AddToCartEvent): void {
    this.zoomProductId.set(null);
    this.prepurchaseModalService.open(event.product, event.quantity);
  }

  protected onNotify(product: Product): void {
    this.stockAlertModal.open(product);
  }

  protected onNotifyCancel(product: Product): void {
    this.stockAlertModal.cancel(product);
  }

  protected onZoomOpen(product: Product): void {
    this.zoomProductId.set(product.id);
  }

  protected onZoomClose(): void {
    this.zoomProductId.set(null);
  }

  protected onZoomViewDetails(product: Product): void {
    this.zoomProductId.set(null);
    this.navigateToProduct(product);
  }

  /**
   * El perfil no vive bajo `/tienda/:category`: el slug de categoría sale del árbol cacheado. Sin slug de producto o
   * sin categoría que resolver no se navega (mejor no moverse que llevar a un 404).
   */
  protected navigateToProduct(product: Product): void {
    if (!product.slug) return;
    const categorySlug = deriveProductCategorySlug(product, this.roots());
    if (!categorySlug) return;
    void this.router.navigate(['/tienda', categorySlug, product.slug]);
  }

  private load(key: ListKey): Observable<ListState> {
    this.zoomProductId.set(null);
    if (key.userId === null) return of<ListState>({ status: 'signed-out' });
    return this.wishlist.list().pipe(
      map((entries): ListState => ({ status: 'loaded', entries })),
      catchError(() => of<ListState>({ status: 'error' })),
      startWith<ListState>({ status: 'loading' }),
    );
  }
}

/**
 * Orden en cliente de la pestaña vieja. Lo que no se puede comprar llega sin precio (enmascarado) y va **siempre al
 * final**, en ascendente y en descendente (AS-W2-4): que no encabece la lista.
 */
function sortEntries(entries: readonly ProfileWishlistEntry[], key: WishlistSortKey): ProfileWishlistEntry[] {
  return [...entries].sort((a, b) => {
    switch (key) {
      case 'date-desc':
        return b.addedAtMs - a.addedAtMs;
      case 'date-asc':
        return a.addedAtMs - b.addedAtMs;
      case 'price-asc':
        return byPrice(a, b, 1);
      case 'price-desc':
        return byPrice(a, b, -1);
      case 'name-asc':
        return a.product.name.localeCompare(b.product.name, 'es');
      case 'name-desc':
        return b.product.name.localeCompare(a.product.name, 'es');
    }
  });
}

/** Precio en `direction` (1 asc, −1 desc); sin precio, al final en los dos sentidos. */
function byPrice(a: ProfileWishlistEntry, b: ProfileWishlistEntry, direction: 1 | -1): number {
  const pa = a.product.price;
  const pb = b.product.price;
  if (pa == null || pb == null) return (pa == null ? 1 : 0) - (pb == null ? 1 : 0);
  return direction * (pa - pb);
}
